import { useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from "react";
import { Controller, type FieldValues } from "react-hook-form";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { mergeSlotProps } from "@mui/material/utils";
import type { FormInputProps } from "./formInput.types";
import { getMuiFormFieldSize } from "../formFieldSize";
import { withRequiredAsterisk } from "../utils";
import FormInputEndAdornment from "./FormInputEndAdornment";
import { comporSxFormInput, submeterFormularioAoPressionarEnter } from "./formInput.helpers";

// ------------------------------- CONSTANTS -------------------------------
const DEFAULT_ACTIVE_SELECTION_COLOR = "var(--mui-palette-warning-main)";

// ------------------------------- COMPONENT -------------------------------
export function FormInput<TFieldValues extends FieldValues>({
	control,
	name,
	rules,
	description,
	leftIcon,
	rightIcon,
	onRightIconClick,
	rightIconAriaLabel = "Action",
	parse,
	format,
	onValueChange,
	fullWidth = true,
	required,
	label,
	labelIncludeRequiredText = false,
	inputRef,
	onBlur,
	onKeyDown,
	multiline,
	inputMode,
	size = "small",
	type = "text",
	maxLength,
	maxLengthText,
	showMaxLengthCounter = false,
	highlightActiveSelection = true,
	activeHighlightColor = DEFAULT_ACTIVE_SELECTION_COLOR,
	upperCaseValue = true,
	reserveHelperText = true,
	...props
}: Readonly<FormInputProps<TFieldValues>>) {
	// -------------------------------- STATES --------------------------------
	const [excedeuMaxLength, setExcedeuMaxLength] = useState(false);

	// ----------------------------- DERIVED STATE ----------------------------
	const formatValueInput = (value: string) => (upperCaseValue ? value.toUpperCase() : value);
	const deveControlarMaxLength = showMaxLengthCounter && typeof maxLength === "number";

	// ------------------------------- HANDLERS -------------------------------
	const tratarTecla = (evento: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		onKeyDown?.(evento);
		if (multiline) submeterFormularioAoPressionarEnter(evento);
	};

	// -------------------------------- RETURN --------------------------------
	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field, fieldState }) => {
				const erroMaxLength =
					excedeuMaxLength && deveControlarMaxLength
						? (maxLengthText ?? `Ultrapassou o limite de ${maxLength} caracteres.`)
						: undefined;
				const errorMsg = fieldState.error?.message ?? erroMaxLength;

				const startAdornment = leftIcon ? (
					<InputAdornment position="start">{leftIcon}</InputAdornment>
				) : undefined;

				const shownValue = format ? format(field.value) : (field.value ?? "");
				const shownValueText = typeof shownValue === "string" ? shownValue : String(shownValue);

				const endAdornment =
					deveControlarMaxLength || rightIcon ? (
						<FormInputEndAdornment
							texto={shownValueText}
							maxLength={maxLength}
							showMaxLengthCounter={showMaxLengthCounter}
							error={Boolean(errorMsg)}
							rightIcon={rightIcon}
							onRightIconClick={onRightIconClick}
							rightIconAriaLabel={rightIconAriaLabel}
						/>
					) : undefined;
				const sxComposto = comporSxFormInput({
					size,
					sx: props.sx,
					value: field.value,
					highlightActiveSelection,
					activeHighlightColor,
					error: Boolean(errorMsg),
				});

				const sizeMUI = getMuiFormFieldSize(size);
				const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
					const raw = event.target.value;
					const valorFormatado = parse ? parse(raw) : formatValueInput(raw);

					if (deveControlarMaxLength && valorFormatado.length > maxLength) {
						setExcedeuMaxLength(true);
						const valorLimitado = valorFormatado.slice(0, maxLength);
						field.onChange(valorLimitado);
						onValueChange?.(valorLimitado);
						return;
					}

					setExcedeuMaxLength(false);
					field.onChange(valorFormatado);
					onValueChange?.(valorFormatado);
				};
				const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
					if (!parse && typeof field.value === "string") {
						const aparado = field.value.trim();
						if (aparado !== field.value) field.onChange(aparado);
					}
					field.onBlur();
					onBlur?.(event);
				};

				return (
					<TextField
						{...props}
						multiline={multiline}
						size={sizeMUI}
						label={withRequiredAsterisk(label, required, labelIncludeRequiredText)}
						name={field.name}
						value={shownValue}
						onChange={handleChange}
						onBlur={handleBlur}
						inputRef={(el) => {
							field.ref(el);
							if (!inputRef) return;
							if (typeof inputRef === "function") {
								inputRef(el);
								return;
							}
							inputRef.current = el;
						}}
						type={type}
						error={!!errorMsg}
						helperText={errorMsg ?? description ?? (reserveHelperText ? " " : undefined)}
						fullWidth={fullWidth}
						slotProps={{
							...props.slotProps,
							input: mergeSlotProps(
								{
									name: field.name,
									value: shownValue,
									onChange: handleChange,
									onBlur: handleBlur,
									...(startAdornment ? { startAdornment } : {}),
									...(endAdornment ? { endAdornment } : {}),
								},
								props.slotProps?.input ?? {},
							),
							htmlInput: mergeSlotProps(
								{
									name: field.name,
									value: shownValue,
									required: !!required,
									...(deveControlarMaxLength ? { maxLength: undefined } : {}),
									...(typeof maxLength === "number" && !deveControlarMaxLength ? { maxLength } : {}),
									...(inputMode ? { inputMode } : {}),
								},
								mergeSlotProps(props.slotProps?.htmlInput ?? {}, { onKeyDown: tratarTecla }),
							),
						}}
						sx={sxComposto}
					/>
				);
			}}
		/>
	);
}
